import React from 'react';
import {
  BadgeCheck,
  CalendarCheck,
  Loader2,
  MessageSquare,
  Percent,
  PhoneCall,
  Rocket,
  Send,
  Undo2,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { formatPrice } from '@/utils/priceHelpers';
import { HelpTooltip } from '@/Components/HelpTooltip';
import { KpiCard } from '../../societies/components/tabs/dashboard/KpiCard';
import type { UpsellGarantDashboard, UpsellMoneyColumns } from '../types/dashboard.types';

interface UpsellSectionProps {
  /** Chiffres du programme upsell (montants en centimes) */
  data: UpsellGarantDashboard | undefined;
  error?: unknown;
  onRetry?: () => void;
  /** Rechargement en arrière-plan (changement de période) */
  isRefreshing?: boolean;
}

const formatCount = (value: number) => value.toLocaleString('fr-FR');

const pluralize = (count: number, word: string) => `${word}${count > 1 ? 's' : ''}`;

const formatRate = (rate: number) => `${rate.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`;

const formatDate = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR');
};

type RowVariant = 'revenue' | 'deduction' | 'net';

const ROW_CLASSES: Record<RowVariant, { row: string; label: string; ttc: string; ht: string }> = {
  revenue: {
    row: 'tw-border-b tw-border-gray-100 tw-bg-affinicar-lighter/30',
    label: 'tw-font-semibold tw-text-affinicar-primary',
    ttc: 'tw-font-medium',
    ht: 'tw-text-gray-500',
  },
  deduction: {
    row: 'tw-border-b tw-border-gray-100 tw-bg-orange-50/30',
    label: 'tw-font-semibold tw-text-orange-700',
    ttc: 'tw-font-medium',
    ht: 'tw-text-gray-500',
  },
  net: {
    row: 'tw-bg-emerald-50 tw-border-t-2 tw-border-emerald-300',
    label: 'tw-font-bold tw-text-emerald-800 sm:tw-text-base',
    ttc: 'tw-font-bold tw-text-emerald-800 sm:tw-text-base',
    ht: 'tw-font-medium tw-text-emerald-700',
  },
};

interface MoneyRowProps {
  label: string;
  tooltip: string;
  tooltipId: string;
  values: UpsellMoneyColumns;
  variant: RowVariant;
  count?: number;
}

const MoneyRow: React.FC<MoneyRowProps> = ({ label, tooltip, tooltipId, values, variant, count }) => {
  const classes = ROW_CLASSES[variant];
  const py = variant === 'net' ? 'tw-py-3' : 'tw-py-2';
  return (
    <tr className={classes.row}>
      <td className={`tw-px-2 sm:tw-px-4 ${py} ${classes.label}`}>
        {label}
        <HelpTooltip content={tooltip} id={tooltipId} />
      </td>
      <td className={`tw-px-2 ${py} tw-text-right tw-font-semibold`}>
        {count !== undefined ? formatCount(count) : ''}
      </td>
      <td className={`tw-px-3 ${py} tw-text-right ${classes.ttc}`}>{formatPrice(values.engaged.ttc)}</td>
      <td className={`tw-px-3 ${py} tw-text-right ${classes.ht}`}>{formatPrice(values.engaged.ht)}</td>
      <td className={`tw-px-3 ${py} tw-text-right tw-bg-emerald-50/30 ${classes.ttc}`}>{formatPrice(values.collected.ttc)}</td>
      <td className={`tw-px-3 ${py} tw-text-right tw-bg-emerald-50/30 ${classes.ht}`}>{formatPrice(values.collected.ht)}</td>
      <td className={`tw-px-3 ${py} tw-text-right tw-bg-amber-50/30 ${classes.ttc}`}>{formatPrice(values.remaining.ttc)}</td>
      <td className={`tw-px-3 ${py} tw-text-right tw-bg-amber-50/30 ${classes.ht}`}>{formatPrice(values.remaining.ht)}</td>
    </tr>
  );
};

/**
 * Onglet « Upsell » du tableau de bord : activité du programme upsell du garant
 * et ce qu'il lui rapporte. Le commercial upsell n'apparaît volontairement pas.
 */
export const UpsellSection: React.FC<UpsellSectionProps> = ({ data, error, onRetry, isRefreshing = false }) => {
  if (error) {
    return (
      <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-6 tw-text-center">
        <p className="tw-text-red-600 tw-font-medium">Erreur lors du chargement des chiffres upsell</p>
        <p className="tw-text-sm tw-text-red-500 tw-mt-2">
          {error instanceof Error ? error.message : 'Erreur inconnue'}
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="tw-mt-4 tw-px-4 tw-py-2 tw-bg-red-600 tw-text-white tw-rounded-md hover:tw-bg-red-700 tw-transition-colors"
          >
            Réessayer
          </button>
        )}
      </div>
    );
  }

  if (!data) {
    return <p className="tw-text-center tw-text-gray-500 tw-py-12">Aucune donnée disponible</p>;
  }

  const { garant, activity, financial } = data;
  const periodFrom = formatDate(data.period.from);
  const periodTo = formatDate(data.period.to);
  const isEmpty = [
    activity.messagesSent,
    activity.appointmentsBooked,
    activity.callsMade,
    activity.clientsReached,
    activity.proposalsSent,
    activity.subscriptions,
    activity.refusals,
    activity.retractions,
    financial.contracts,
    financial.retracted.count,
  ].every((value) => !value);
  const rateLabel = formatRate(garant.commissionRate);
  const retractedCount = financial.retracted.count;

  return (
    <div>
      {/* Bandeau titre */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-bg-affinicar-primary tw-text-white tw-uppercase tw-font-bold tw-px-4 tw-py-4 tw-rounded-lg tw-mb-4 tw-min-h-[56px]">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Rocket className="tw-w-4 sm:tw-w-5 tw-h-4 sm:tw-h-5 tw-shrink-0" />
          <span className="tw-text-sm sm:tw-text-base tw-font-bold tw-leading-none">Programme upsell</span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-normal-case tw-font-medium tw-text-xs sm:tw-text-sm">
          {isRefreshing && <Loader2 size={16} className="tw-animate-spin" />}
          {periodFrom && periodTo && <span>Du {periodFrom} au {periodTo}</span>}
        </div>
      </div>

      {isEmpty ? (
        <p className="tw-text-center tw-text-gray-500 tw-py-12">Aucune activité upsell sur la période sélectionnée</p>
      ) : (
        <>
          {/* ─── Activité ─── */}
          <div className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-3">Activité</div>
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4 tw-mb-6">
            <KpiCard
              title="Messages envoyés"
              value={formatCount(activity.messagesSent)}
              subtitle={`dont ${formatCount(activity.messagesBySms)} par SMS`}
              icon={MessageSquare}
              color="blue"
            />
            <KpiCard
              title="Rendez-vous pris"
              value={formatCount(activity.appointmentsBooked)}
              subtitle={`dont ${formatCount(activity.appointmentsCancelled)} ${pluralize(activity.appointmentsCancelled, 'annulé')}`}
              icon={CalendarCheck}
              color="affinicar"
            />
            <KpiCard title="Appels passés" value={formatCount(activity.callsMade)} icon={PhoneCall} color="blue" />
            <KpiCard title="Clients joints" value={formatCount(activity.clientsReached)} icon={UserCheck} color="affinicar" />
            <KpiCard title="Propositions envoyées" value={formatCount(activity.proposalsSent)} icon={Send} color="purple" />
            <KpiCard title="Souscriptions" value={formatCount(activity.subscriptions)} icon={BadgeCheck} color="green" />
            <KpiCard title="Refus" value={formatCount(activity.refusals)} icon={XCircle} color="red" />
            <KpiCard title="Rétractations" value={formatCount(activity.retractions)} icon={Undo2} color="orange" />
            <KpiCard
              title="Taux de conversion"
              value={activity.conversionRate === null ? '—' : formatRate(activity.conversionRate)}
              subtitle="Souscriptions / propositions envoyées"
              icon={Percent}
              color="green"
            />
          </div>

          {/* ─── Résultats financiers ─── */}
          <div className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-3">Résultats financiers</div>

          <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-overflow-x-auto tw-mb-4">
            <table className="tw-w-full tw-text-xs sm:tw-text-sm tw-min-w-[860px]">
              <thead>
                <tr className="tw-border-b tw-border-gray-200">
                  <th className="tw-px-4 tw-py-2" colSpan={2}></th>
                  <th className="tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-gray-600 tw-bg-gray-100" colSpan={2}>
                    ENGAG&Eacute; (FACTUR&Eacute;)
                  </th>
                  <th className="tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-emerald-700 tw-bg-emerald-50" colSpan={2}>
                    ENCAISS&Eacute;
                  </th>
                  <th className="tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-amber-700 tw-bg-amber-50" colSpan={2}>
                    &Agrave; ENCAISSER
                  </th>
                </tr>
                <tr className="tw-border-b tw-border-gray-300 tw-text-xs tw-uppercase">
                  <th className="tw-px-2 sm:tw-px-4 tw-py-2 tw-text-left tw-text-gray-500 tw-min-w-[220px]"></th>
                  <th className="tw-px-2 tw-py-2 tw-text-right tw-text-gray-500 tw-w-16">Nb</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-gray-100/50">TTC</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-gray-100/50">HT</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600 tw-bg-emerald-50/50">TTC</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600 tw-bg-emerald-50/50">HT</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600 tw-bg-amber-50/50">TTC</th>
                  <th className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600 tw-bg-amber-50/50">HT</th>
                </tr>
              </thead>
              <tbody>
                <MoneyRow
                  label="Chiffre d'affaires des souscriptions"
                  tooltip="Prix des garanties souscrites via le programme upsell sur la période. Engagé : montant facturé au client ; encaissé : déjà payé ; à encaisser : mensualités restantes."
                  tooltipId="tooltip-upsell-revenue"
                  values={financial.revenue}
                  count={financial.contracts}
                  variant="revenue"
                />
                <MoneyRow
                  label={`Notre commission upsell (${rateLabel})`}
                  tooltip={`Commission de ${rateLabel} sur chaque garantie souscrite via l'upsell.`}
                  tooltipId="tooltip-upsell-commission"
                  values={financial.commission}
                  variant="deduction"
                />
                <MoneyRow
                  label="Frais de plateforme (paiement en ligne / mensualisation)"
                  tooltip="Frais de service prélevés sur les paiements en ligne et sur les mensualités des souscriptions upsell."
                  tooltipId="tooltip-upsell-platform-fees"
                  values={financial.platformFees}
                  variant="deduction"
                />
                <MoneyRow
                  label={`Montant gagné par ${garant.name}`}
                  tooltip={`Ce qui revient à ${garant.name} sur les souscriptions upsell de la période.`}
                  tooltipId="tooltip-upsell-garant-net"
                  values={financial.garantNet}
                  variant="net"
                />
              </tbody>
            </table>
          </div>

          <div className="tw-flex tw-items-start tw-gap-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-px-4 tw-py-3 tw-text-sm tw-text-gray-700">
            <Undo2 className="tw-w-4 tw-h-4 tw-mt-0.5 tw-shrink-0 tw-text-orange-600" />
            <p className="tw-mb-0">
              <span className="tw-font-semibold">Rétractations :</span>{' '}
              {formatCount(retractedCount)} {pluralize(retractedCount, 'contrat')} — {formatPrice(financial.retracted.amount.ttc)} TTC souscrits,{' '}
              {formatPrice(financial.retracted.refunded.ttc)} TTC remboursés
            </p>
          </div>
        </>
      )}
    </div>
  );
};
